import { usePublicSettings } from '../../core/hooks/use-public-settings';
import { useTranslation } from '../../core/hooks/use-translation';
import { SettingKeys } from '../../core/models/setting-keys';
import { company, telHref } from '../../site/company';
import { Panel } from '../../ui/panel';
import { ChatIcon, PhoneIcon } from '../../ui/portal-icons';

/**
 * How to reach a person, on every portal screen rather than only on the empty one.
 *
 * **The telephone number used to appear only when a customer had no cases**, tucked inside the
 * empty state, which is precisely backwards: somebody with an open file and a question about it is
 * the person most likely to want to ring. It is a panel of its own now, on both portal screens.
 *
 * **The settings value is preferred and the page's own copy is the fallback.** The company's number
 * is content on the marketing pages for a reason — they answer with the server down — and the
 * portal inherits that: a failed settings read used to leave this screen with no number on it at
 * all, which is the moment it is least affordable.
 */
export function ContactCard() {
    const { t } = useTranslation();
    const { settings } = usePublicSettings();

    const phone = settings.values[SettingKeys.contactPhone] ?? company.phone;
    const whatsApp = settings.values[SettingKeys.contactWhatsApp] ?? company.whatsApp;

    return (
        <Panel title={t('portal.contact_title')} icon={<PhoneIcon />}>
            <p className="text-sm text-slate-600 dark:text-slate-400">{t('portal.contact_hint')}</p>

            <div className="mt-4 space-y-2">
                <a
                    href={telHref(phone)}
                    className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2.5 font-semibold text-slate-900 transition hover:border-brand-300 hover:text-brand-700 dark:border-slate-800 dark:text-slate-100 dark:hover:border-brand-700 dark:hover:text-brand-300"
                >
                    <PhoneIcon className="size-4 text-brand-600 dark:text-brand-400" />
                    {phone}
                </a>

                <a
                    href={whatsApp}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2.5 font-semibold text-slate-900 transition hover:border-brand-300 hover:text-brand-700 dark:border-slate-800 dark:text-slate-100 dark:hover:border-brand-700 dark:hover:text-brand-300"
                >
                    <ChatIcon className="size-4 text-brand-600 dark:text-brand-400" />
                    WhatsApp
                </a>
            </div>
        </Panel>
    );
}
